import { useEffect, useRef } from 'react';

import { filterPickRows, type PickRow } from '../model/builder.js';

/**
 * The slot picker (design 1517–1538).
 *
 * A title, a filter box, and rows. The design's own placeholder states the two
 * things the box does — "Type to filter · Enter takes the first" — and that is
 * the whole of the instruction, because a picker with a caption explaining that
 * rows are clickable is the console explaining itself.
 *
 * ## The empty case is said once, and this file does not decide what it says
 *
 * One sentence, in the design's place, and it is the *only* thing rendered when
 * the list comes back empty. There is no second line explaining which condition
 * bit: that would be narrating the mechanism.
 *
 * Which sentence is `pickerEmptyReason`'s to answer, not this component's. There
 * are two, because there are two genuinely different situations and the default
 * one is false in the second: a filter that excluded everything, where the next
 * move is to change a slot, and a seat that tasks nothing at all, where there is
 * no next move and saying "try another combination" sends the operator round a
 * loop. A presentational component asking that question would be the second place
 * asking it.
 */

export interface PickerPanelProps {
  readonly title: string;
  readonly rows: readonly PickRow[];
  /** What to say when the list is empty — `pickerEmptyReason`'s answer. */
  readonly empty: string;
  readonly query: string;
  readonly onQuery: (query: string) => void;
  readonly onChoose: (row: PickRow) => void;
  readonly onClose: () => void;
}

export function PickerPanel(props: PickerPanelProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  // The same list the shell's `1–9` binding indexes into, by the same function —
  // two lists computed separately would drift the first time one side filtered.
  const visible = filterPickRows(props.rows, props.query);

  useEffect(() => {
    inputRef.current?.focus();
  }, [props.title]);

  return (
    <div
      data-testid="picker"
      data-picker-title={props.title}
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border-strong)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 28,
          padding: '0 8px 0 12px',
          background: 'var(--surface-3)',
          borderBottom: '1px solid var(--rule)',
        }}
      >
        <span
          style={{
            flex: 1,
            fontFamily: 'var(--font-ui)',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'var(--text-2)',
          }}
        >
          {props.title}
        </span>
        <button
          type="button"
          data-testid="picker-close"
          onClick={props.onClose}
          style={{
            height: 20,
            padding: '0 8px',
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-data)',
            fontSize: 11,
            letterSpacing: '.08em',
            cursor: 'pointer',
          }}
        >
          ESC
        </button>
      </div>

      <input
        ref={inputRef}
        type="text"
        data-testid="picker-query"
        aria-label={props.title}
        value={props.query}
        onChange={(event) => props.onQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          const first = visible[0];
          if (first !== undefined) props.onChoose(first);
        }}
        placeholder="Type to filter · Enter takes the first"
        style={{
          height: 28,
          margin: '8px 12px',
          padding: '0 8px',
          background: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          color: 'var(--text)',
          fontFamily: 'var(--font-prose)',
          fontSize: 13,
        }}
      />

      <div style={{ maxHeight: 264, overflowY: 'auto' }}>
        {visible.length === 0 && (
          <div
            data-testid="picker-empty"
            style={{
              padding: 12,
              fontFamily: 'var(--font-prose)',
              fontSize: 13,
              color: 'var(--text-muted)',
            }}
          >
            {props.empty}
          </div>
        )}
        {visible.map((row, index) => (
          <button
            key={row.key}
            type="button"
            data-testid="picker-row"
            data-pick-id={row.id}
            onClick={() => props.onChoose(row)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              minHeight: 32,
              padding: '0 8px 0 12px',
              background: 'transparent',
              border: 0,
              borderBottom: '1px solid var(--rule-faint)',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                flex: 'none',
                width: 14,
                fontFamily: 'var(--font-data)',
                fontSize: 10,
                color: 'var(--text-disabled)',
              }}
            >
              {index < 9 ? String(index + 1) : ''}
            </span>
            <span
              style={{
                flex: 'none',
                fontFamily: 'var(--font-data)',
                fontSize: 12,
                fontWeight: 600,
                color: rowColour(row.tone),
              }}
            >
              {row.id}
            </span>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontFamily: 'var(--font-prose)',
                fontSize: 13,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'var(--text-2)',
              }}
            >
              {row.desc}
            </span>
            <span
              style={{
                flex: 'none',
                fontFamily: 'var(--font-data)',
                fontSize: 11,
                letterSpacing: '.04em',
                whiteSpace: 'nowrap',
                color: 'var(--text-3)',
              }}
            >
              {row.meta}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function rowColour(tone: PickRow['tone']): string {
  switch (tone) {
    case 'friendly':
      return 'var(--blue)';
    case 'hostile':
      return 'var(--red)';
    case 'mission':
      return 'var(--primary)';
    default:
      return 'var(--text-2)';
  }
}
